import React, { useState } from 'react';
import { Box, Card, CardContent, Typography, Button, Chip, Alert } from '@mui/material';
import { Upload as UploadIcon, Save as SaveIcon } from '@mui/icons-material';
import { scheduleOpt, AI_BASE_URL } from '../../services/aiBackend';
import api from '../../services/api';

const ScheduleOptimizationPage: React.FC = () => {
  const [xerFile, setXerFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [response, setResponse] = useState<any>(null);

  const handleXerChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    setXerFile(file);
  };

  const runOptimization = async () => {
    if (!xerFile) return;
    try {
      setLoading(true);
      setError(null);
      const res = await scheduleOpt(xerFile);
      setResponse(res);
      // Persist result in backend AIAnalysis
      await api.post('/ai-analysis', {
        name: `Schedule Optimization - ${xerFile.name}`,
        analysisType: 'schedule_optimization',
        description: 'Result from external AI backend schedule optimization',
        inputParameters: { fileName: xerFile.name },
        algorithms: { primary: 'critical_path_method' },
        dataSources: ['external_ai_backend', 'uploaded_files'],
        executionSettings: { initiatedFrom: 'frontend', externalBaseUrl: AI_BASE_URL },
        results: res.data,
      });
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to run optimization');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>Schedule Optimization</Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
      )}
      <Card>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>Upload .xer file</Typography>
          <input type="file" accept=".xer" onChange={handleXerChange} />
          {xerFile && (
            <Box sx={{ mt: 2 }}>
              <Chip label={xerFile.name} variant="outlined" />
            </Box>
          )}
          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
            <Button variant="contained" startIcon={<UploadIcon />} disabled={!xerFile || loading} onClick={runOptimization}>
              Run Optimization
            </Button>
            <Button variant="outlined" startIcon={<SaveIcon />} disabled={!response} onClick={() => { /* placeholder */ }}>
              Save Result (Auto)
            </Button>
          </Box>
          {response && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2">Response Status: {response.status}</Typography>
              <pre style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(response.data, null, 2)}</pre>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ScheduleOptimizationPage;